import { create } from "zustand";
import { persist } from "zustand/middleware";
import { logger } from "../../utils";
// ─────────────────────────────────────────────────────────────
// TOKEN STORAGE NOTE (Security Debt: TASK-P0-03)
// ─────────────────────────────────────────────────────────────
// Saat ini token disimpan di localStorage via Zustand persist.
// Risiko: rentan XSS jika ada script injection.
//
// Mitigasi aktif:
//   1. clearSession() membuang token dari state DAN memanggil
//      storage.removeItem() secara eksplisit untuk memastikan
//      localStorage benar-benar bersih (belt-and-suspenders).
//   2. setSession() memvalidasi bahwa token tidak kosong sebelum
//      disimpan — mencegah penyimpanan state yang korup/tidak valid.
//   3. Setiap event sesi kritis di-log untuk auditability.
//
// Migration path (future):
//   Untuk migrasi ke httpOnly cookie, ganti persist storage
//   dengan custom storage adapter yang mengarah ke backend
//   session endpoint. Flag: PENA_AUTH_USE_COOKIE_SESSION = false
// ─────────────────────────────────────────────────────────────
const PORTAL_AUTH_STORAGE_KEY = "portal-auth-storage";
export const usePortalAuthStore = create()(persist((set) => ({
    user: null,
    lembaga: null,
    accessToken: null,
    accessExpiredAt: null,
    refreshToken: null,
    refreshExpiredAt: null,
    hydrated: false,
    authStage: "IDLE",
    mfaToken: null,
    mfaChallengeId: null,
    currentCabang: undefined,
    availableCabang: [],
    setCabang: (cabang) => set({ currentCabang: cabang }),
    terkaitList: [],
    setTerkaitList: (list) => set({ terkaitList: list }),
    setHydrated: (value) => set({ hydrated: value }),
    setMfaEnabled: (status) => set((state) => ({
        user: state.user ? { ...state.user, mfaEnabled: status } : null
    })),
    updateUser: (userData) => set((state) => ({
        user: state.user ? { ...state.user, ...userData } : null
    })),
    setMfaPending: (mfaToken, challengeId) => {
        logger.info("[AUTH] Memasuki tahap verifikasi MFA.", { challengeId });
        set({
            authStage: "MFA_PENDING",
            mfaToken,
            mfaChallengeId: challengeId || null,
            accessToken: null,
            accessExpiredAt: null,
            refreshToken: null,
            refreshExpiredAt: null,
        });
    },
    setSession: (data) => {
        // Guard: tolak penyimpanan jika accessToken tidak valid
        if (!data.accessToken) {
            logger.warn("[AUTH] setSession dipanggil dengan token kosong — sesi tidak disimpan.", { hasAccessToken: !!data.accessToken });
            return;
        }
        logger.info("[AUTH] Sesi dimulai.", {
            userId: data.user?.user_cms,
            idLembaga: data.lembaga?.idLembaga,
            accessExpiredAt: data.accessExpiredAt,
        });
        set({
            authStage: "AUTHENTICATED",
            user: data.user,
            lembaga: data.lembaga,
            accessToken: data.accessToken,
            accessExpiredAt: data.accessExpiredAt,
            refreshToken: data.refreshToken || null,
            refreshExpiredAt: data.refreshExpiredAt || null,
            currentCabang: data.lembaga?.cabang?.namaCabang,
            availableCabang: [], // Portal doesn't use availableCabangList
            // Reset MFA state upon successful authentication
            mfaToken: null,
            mfaChallengeId: null,
        });
    },
    clearSession: () => {
        logger.info("[AUTH] Sesi dihapus — token dan data pengguna dibersihkan.");
        // Belt-and-suspenders: hapus storage key secara eksplisit.
        // Zustand persist akan mengupdate storage saat state berubah,
        // tapi removeItem memastikan tidak ada sisa data di localStorage.
        try {
            localStorage.removeItem(PORTAL_AUTH_STORAGE_KEY);
        }
        catch {
            // Diabaikan: environment tanpa localStorage (SSR/test)
        }
        set({
            authStage: "IDLE",
            user: null,
            lembaga: null,
            accessToken: null,
            accessExpiredAt: null,
            refreshToken: null,
            refreshExpiredAt: null,
            currentCabang: undefined,
            availableCabang: [],
            mfaToken: null,
            mfaChallengeId: null,
            terkaitList: [],
        });
    },
}), {
    name: PORTAL_AUTH_STORAGE_KEY,
    partialize: (state) => ({
        user: state.user,
        lembaga: state.lembaga,
        currentCabang: state.currentCabang,
        availableCabang: state.availableCabang,
        authStage: state.authStage,
        terkaitList: state.terkaitList,
    }),
    onRehydrateStorage: () => (state) => {
        if (state)
            state.setHydrated(true);
    },
}));
